import { normalizeApiOrigin } from '../../../shared/lib/apiOrigin';
import { apiUrl, getApiBase } from './api';

/**
 * Origem opcional da API de produção para:
 * - GET /api/admin/catalog (leitura Dynamo → painel)
 * - POST /api/admin/sync (DynamoDB)
 * - POST /api/admin/media/upload (S3)
 *
 * Assim o admin continua usando o proxy local (`VITE_API_URL` vazio → :5000) para o restante,
 * mas você pode apontar só ingestão para produção (ex.: `.env.local` com a URL do API Gateway).
 */
export function getAdminRemoteIngestBase(): string {
  const raw = import.meta.env.VITE_ADMIN_SYNC_URL;
  if (typeof raw === 'string' && raw.trim() !== '') {
    return normalizeApiOrigin(raw.trim());
  }
  return '';
}

/** True se há origem de API para catálogo/sync (env no build, URL dedicada de sync, ou host prod NaviExperience + inferência em `getApiBase`). */
export function isAdminApiIngestConfigured(): boolean {
  const api = typeof import.meta.env.VITE_API_URL === 'string' ? import.meta.env.VITE_API_URL.trim() : '';
  if (api !== '') return true;
  if (getAdminRemoteIngestBase() !== '') return true;
  return getApiBase() !== '';
}

/**
 * API Gateway envia o path com o estágio antes de `/api/...` (ex. `/prod/api/admin/catalog`).
 * Domínio customizado sem esse segmento: não acrescentar nada.
 * `VITE_ADMIN_PRODUCTION_API_GATEWAY_STAGE=none` — não prefixar (ex.: HTTP API $default).
 * Usado com `VITE_ADMIN_SYNC_URL` quando o host é `*.execute-api.*`.
 */
export function adminRemoteStagePrefixIfNeeded(remoteBaseNormalized: string): string {
  let u: URL;
  try {
    const s = remoteBaseNormalized.trim().startsWith('http')
      ? remoteBaseNormalized.trim()
      : `https://${remoteBaseNormalized.trim()}`;
    u = new URL(s);
  } catch {
    return '';
  }
  const segments = u.pathname.replace(/\/+$/, '').split('/').filter(Boolean);
  if (segments.length > 0) return '';
  if (!/\.execute-api\./i.test(u.hostname)) return '';
  const envStage = import.meta.env.VITE_ADMIN_PRODUCTION_API_GATEWAY_STAGE;
  if (envStage === 'none' || envStage === '_') return '';
  const stage = typeof envStage === 'string' && envStage.trim() !== '' ? envStage.trim() : 'prod';
  return `/${stage}`;
}

/** URL para sync/upload remoto; se `VITE_ADMIN_SYNC_URL` não estiver definida, usa a mesma base que `apiUrl`. */
export function adminIngestApiUrl(path: string): string {
  const remote = getAdminRemoteIngestBase();
  const p = path.startsWith('/') ? path : `/${path}`;
  if (remote) {
    const stage = adminRemoteStagePrefixIfNeeded(remote);
    return `${remote}${stage}${p}`;
  }
  return apiUrl(path);
}
